<template>
  <div class="row mr-0 ml-0 sessionStyle" @click="makeOrEditBet">
    <Popper
      v-if="Math.round(positionNet) !== 0"
      hover
      arrow
      placement="top"
      :open-delay="100"
      class="position-net"
      :class="positionNet > 0 ? 'position-net-win' : 'position-net-lose'"
    >
      <span class="position-net-badge" @click.stop="handlePositionBadgeClick">
        {{ positionNet > 0 ? '+' : '-' }}{{ formattedPositionNet }}
      </span>
      <template #content>
        {{ positionNetTooltip }}
      </template>
    </Popper>
    <div
      v-if="hasSelfSession(totals, userStore.user.id)"
      :class="[`total-session-ownUser`]"
    >
      {{ capitalize(totalComputed) }} {{ total.total }}
      {{ isMobile && !league.includes('PROPS') ? '|' : '' }}
      <br v-if="isMobile && league.includes('PROPS')" />
      {{ displayOdds }}
    </div>
    <div
      v-else-if="hasMarketMakerSession(totals)"
      :class="[`total-session-marketmaker`]"
    >
      {{ capitalize(totalComputed) }} {{ total.total }}
      {{ isMobile && !league.includes('PROPS') ? '|' : '' }}
      <br v-if="isMobile && league.includes('PROPS')" />
      {{ displayOdds }}
    </div>
    <span
      v-if="lineSwitchable"
      class="line-chevron"
      role="button"
      aria-label="Change total line"
      @click="onChevronTap"
    >
      <font-awesome-icon :icon="faChevronDown" />
    </span>
  </div>
</template>

<script setup>
import { useUserStore } from '@/stores/user';
import { computed, inject } from 'vue';
import { formatOdds, formatOddsRounded } from '@/core/oddsFormatUtils';
import {
  hasSelfSession,
  hasMarketMakerSession,
  addComa,
  capitalize,
} from '@/core/utils';
import { useRoute } from 'vue-router';
import Popper from 'vue3-popper';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';

const userStore = useUserStore();
const route = useRoute();
const openMarketPositionModal = inject('openMarketPositionModal', null);

const props = defineProps({
  totals: {
    type: Array || Object,
    default: null,
  },
  league: {
    type: String,
    required: true,
  },
  side: {
    default: '',
    type: String,
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  shortNumber: {
    type: Boolean,
    default: false,
  },
  // Rounds odds to whole numbers on the homepage cards where decimal odds make the cells too wide
  roundedHomepageOdds: {
    type: Boolean,
    default: false,
  },
  // Net win/loss for the user if this side of the market hits
  positionNet: {
    type: Number,
    default: 0,
  },
  // When true, clicking the "O 6.5" label opens the line ladder instead of
  // the bet flow (the rest of the cell still bets as usual).
  lineSwitchable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['makeBet', 'editOrderModal', 'openLadder']);

const shortNumber = computed(() => {
  return (
    props.shortNumber ||
    route.name === 'HomePage' ||
    (props.isMobile && props.league.includes('PROPS'))
  );
});

const totalComputed = computed(() => {
  if (shortNumber.value) {
    return total.value.OU === 'under' ? 'U' : 'O';
  }
  return total.value.OU;
});

const total = computed(() => {
  return Array.isArray(props.totals) ? props.totals[0] : props.totals;
});

const displayOdds = computed(() => {
  const fn = props.roundedHomepageOdds ? formatOddsRounded : formatOdds;
  return fn(total.value.odds, props.league, 'total', false, userStore);
});

const positionNetTooltip = computed(() => {
  const amount = Number(Math.abs(props.positionNet)).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return props.positionNet > 0
    ? `If this side hits, you win $${amount}`
    : `If this side hits, you lose $${amount}`;
});

const formattedPositionNet = computed(() =>
  addComa(Math.abs(props.positionNet)),
);

function makeOrEditBet() {
  const selfSession = hasSelfSession(props.totals, userStore.user.id, true);
  if (selfSession) {
    emit('editOrderModal', selfSession);
  } else {
    emit('makeBet', props.totals);
  }
}

function handlePositionBadgeClick() {
  if (typeof openMarketPositionModal === 'function') {
    openMarketPositionModal(total.value?.type || 'total');
  }
}

function onChevronTap(event) {
  if (!props.lineSwitchable) return;
  event.stopPropagation();
  emit('openLadder');
}
</script>

<style scoped lang="scss">
@use '@/scss/tokens.scss' as main;

.total-session-marketmaker,
.total-session-ownUser,
.total-session-filler {
  padding: 0.5rem;
  position: relative;
}

.sessionStyle {
  width: fit-content;
  align-items: center;
  justify-content: center;
  overflow: visible !important;
  position: relative;
}

.position-net {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  margin: 0 !important;
  border: none !important;
}

.position-net-badge {
  display: inline-block;
  font-size: 8px;
  line-height: 1;
  font-weight: bold;
  padding: 2px 4px 3px 3px;
  border-bottom-right-radius: 6px;
  cursor: help;
}

.position-net-win .position-net-badge {
  color: main.$green-500;
  background: color-mix(in srgb, var(--green-500) 16%, transparent);
}

.position-net-lose .position-net-badge {
  color: main.$red-500;
  background: color-mix(in srgb, var(--red-500) 16%, transparent);
}

.position-net :deep(.popper) {
  background: main.$white_01 !important;
  color: main.$black-102 !important;
  border: 1px main.$grey-101 solid !important;
  border-radius: 4px !important;
  box-shadow: 1px 1px 5px 1px main.$blue-100 !important;
  padding: 6px 8px !important;
  font-size: main.$font-size-xs !important;
  font-weight: main.$font-weight-normal !important;
  width: max-content !important;
  max-width: 14rem !important;
  text-align: center;
}

.position-net :deep(.popper #arrow::before) {
  background: main.$white_01;
}

.size {
  font-size: 6px;
  margin-top: 2px;
}

// The line-switch trigger: a light chevron in the cell's right corner,
// revealed only while THIS cell is hovered. Clicking it opens the ladder;
// the rest of the cell still bets.
.line-chevron {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  padding: 6px;
  font-size: 9px;
  line-height: 1;
  color: currentColor;
  opacity: 0;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.sessionStyle:hover .line-chevron {
  opacity: 0.4;
}

.sessionStyle .line-chevron:hover {
  opacity: 0.8;
}
</style>
